import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { Claim } from 'fhir/r4';
import { SYSTEM_CPT, SYSTEM_HCPCS, SYSTEM_ICD10_CM } from '../system-urls';
import { FHIRModel } from './fhir-model';

/**
 * Wraps a FHIR `Claim` for use as risk gap evidence. The extracted fields
 * mirror `summarizeClaim` from `@zeus-health/types` (which powers the card's
 * copy / add-to-note text), so the card and the summary stay consistent.
 */
export class ClaimModel extends FHIRModel<Claim> {
  public kind = 'Claim' as const;

  public get title(): string {
    return this.icd10Codes.length > 0 ? `ICD-10 ${this.icd10Codes.join(', ')}` : 'Claim';
  }

  /** ICD-10-CM codes from `diagnosis[].diagnosisCodeableConcept.coding`, deduplicated. */
  public get icd10Codes(): Array<string> {
    const seen = new Set<string>();
    for (const d of this.resource.diagnosis ?? []) {
      for (const c of d.diagnosisCodeableConcept?.coding ?? []) {
        if (c.system === SYSTEM_ICD10_CM && c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  /** CPT (HCPCS Level I) + HCPCS Level II codes from `item[].productOrService.coding`, deduplicated. */
  public get serviceCodes(): Array<string> {
    const seen = new Set<string>();
    for (const item of this.resource.item ?? []) {
      for (const c of item.productOrService?.coding ?? []) {
        if ((c.system === SYSTEM_CPT || c.system === SYSTEM_HCPCS) && c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  /** Date of service: `billablePeriod.end`, formatted `M/d/yyyy` (matches `summarizeClaim`). */
  public get serviceDate(): string | undefined {
    return this.resource.billablePeriod?.end
      ? formatFHIRDate(this.resource.billablePeriod.end)
      : undefined;
  }
}
